const EMBED_SCROLL_TOP_SELECTORS = [
  "#search-header",
  "header.app-header",
  "#search-area",
  "#header",
  "#app",
] as const;

/**
 * CSS custom property (on `:root` / `html` / `body`) for the extra upward scroll
 * offset. Branded embeds can override via Lua-injected CSS, e.g.:
 * `body { --embed-scroll-top-offset: 80px; }`
 */
export const EMBED_SCROLL_TOP_OFFSET_CSS_VAR = "--embed-scroll-top-offset";

/** Fallback when the CSS variable is missing or invalid. */
export const EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT = 80;

export type RequestEmbedScrollTopOptions = {
  /**
   * Scroll animation for in-document scrolls. Prefer `"auto"` on page changes so
   * the jump is not interrupted by layout/overlays. Default `"auto"`.
   */
  behavior?: ScrollBehavior;
  /**
   * Re-apply after the next paint(s) so async layout / history restoration cannot
   * leave the viewport mid-page after a route change. Default `true`.
   */
  defer?: boolean;
};

/**
 * Resolves the embed scroll-top offset from CSS, then falls back to 80px.
 * Accepts unitless numbers or `px` (e.g. `80` / `80px`).
 */
export function getEmbedScrollTopExtraOffsetPx(): number {
  if (typeof document === "undefined") {
    return EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT;
  }

  const fromEl = (el: Element | null): string => {
    if (!el) return "";
    return getComputedStyle(el)
      .getPropertyValue(EMBED_SCROLL_TOP_OFFSET_CSS_VAR)
      .trim();
  };

  const raw =
    fromEl(document.documentElement) ||
    fromEl(document.body) ||
    `${EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT}px`;

  const match = raw.match(/^(-?\d+(?:\.\d+)?)(px)?$/i);
  if (!match) return EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT;

  const n = Number(match[1]);
  if (!Number.isFinite(n) || n < 0) {
    return EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT;
  }
  return Math.round(n);
}

/** @deprecated Prefer `getEmbedScrollTopExtraOffsetPx()` — kept for call sites/docs. */
export const EMBED_SCROLL_TOP_EXTRA_OFFSET_PX =
  EMBED_SCROLL_TOP_EXTRA_OFFSET_PX_DEFAULT;

function isEmbeddedWindow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/**
 * Force this document to the top (standalone page or iframe document).
 * Covers `window`, `html`, `body`, and common overflow scroll parents.
 */
function scrollLocalDocumentTop(behavior: ScrollBehavior): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  try {
    window.scrollTo({ top: 0, left: 0, behavior });
  } catch {
    window.scrollTo(0, 0);
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  const overflowParents = document.querySelectorAll<HTMLElement>(
    "#app, main, .app-main-content, .app-page-frame",
  );
  overflowParents.forEach((el) => {
    if (el.scrollTop > 0) {
      el.scrollTop = 0;
    }
  });
}

/**
 * Scroll the iframe document to a header/top anchor. Older host pages that do
 * not handle `{ scrollTop: true }` still need an in-iframe scroll target.
 * @returns true when a target was found and scrolled into view
 */
function scrollIntoViewEmbedTop(behavior: ScrollBehavior): boolean {
  if (typeof document === "undefined") return false;
  for (const selector of EMBED_SCROLL_TOP_SELECTORS) {
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior, block: "start" });
      return true;
    }
  }
  return false;
}

/**
 * If the document can still scroll up, nudge further so sticky host chrome
 * does not cover the top of the calculator.
 */
function scrollEmbedExtraOffsetUp(behavior: ScrollBehavior): void {
  if (typeof window === "undefined") return;
  const offset = getEmbedScrollTopExtraOffsetPx();
  if (offset <= 0) return;
  const current = window.scrollY || document.documentElement.scrollTop || 0;
  if (current <= 0) return;
  const next = Math.max(0, current - offset);
  if (next === current) return;
  try {
    window.scrollTo({ top: next, behavior });
  } catch {
    window.scrollTo(0, next);
  }
}

function postScrollTopToParent(offset: number): void {
  if (!isEmbeddedWindow()) return;
  window.parent.postMessage(
    {
      scrollTop: true,
      offset,
    },
    "*",
  );
}

/**
 * One pass of local (+ optional embed) scroll-to-top.
 * Standalone: hard document top only (no scrollIntoView — fights scroll-padding /
 * sticky header and can leave a gap). Embedded: intoView for legacy hosts, then
 * re-assert local top + parent postMessage.
 */
function applyEmbedScrollTop(behavior: ScrollBehavior): void {
  const offset = getEmbedScrollTopExtraOffsetPx();
  const embedded = isEmbeddedWindow();

  scrollLocalDocumentTop(behavior);

  if (embedded) {
    scrollIntoViewEmbedTop(behavior);
    // scrollIntoView + scroll-padding can leave a non-zero scrollY — pin again.
    scrollLocalDocumentTop(behavior);
    postScrollTopToParent(offset);
  }
}

/**
 * Ask the embed (and parent, when embedded) to scroll to the top.
 *
 * Works for: no iframe, classic iframe hosts (ignore postMessage), and modern
 * hosts that listen for `{ scrollTop: true, offset }`.
 *
 * Posts directly so repeated opens are not skipped by postMessageWrapper dedupe.
 */
export function requestEmbedScrollTop(
  options: RequestEmbedScrollTopOptions = {},
): void {
  const behavior = options.behavior ?? "auto";
  const defer = options.defer !== false;
  const offset = getEmbedScrollTopExtraOffsetPx();

  applyEmbedScrollTop(behavior);

  if (!defer || typeof window === "undefined") {
    return;
  }

  // Beat history scroll restoration and late layout from the new route view.
  requestAnimationFrame(() => {
    applyEmbedScrollTop(behavior);
    requestAnimationFrame(() => {
      applyEmbedScrollTop(behavior);
      if (isEmbeddedWindow()) {
        scrollEmbedExtraOffsetUp(behavior);
        postScrollTopToParent(offset);
      }
      window.setTimeout(() => {
        applyEmbedScrollTop(behavior);
        if (isEmbeddedWindow()) {
          postScrollTopToParent(offset);
        }
      }, 50);
    });
  });
}

/**
 * Host pages (e.g. portal) scroll a tall iframe; Bootstrap/fixed modals sit at the
 * top of the iframe and are off-screen unless the host scrolls up.
 */
export function scrollToTopForModal(): void {
  requestEmbedScrollTop({ behavior: "auto" });
}
